import { Link } from 'react-router-dom';
import type { Comment } from '../lib/types';
import Markdown from './Markdown';

interface Props {
  comments: Comment[];
  projectSlug?: string;
}

export default function CommentThread({ comments, projectSlug }: Props) {
  if (comments.length === 0) {
    return (
      <div className="text-sm text-slate-400 italic">No comments yet.</div>
    );
  }
  return (
    <div className="space-y-2">
      {comments.map((c) => {
        const isSystem = c.kind === 'system_event';
        const isApproval = c.kind.startsWith('approval_');
        const borderColor = isSystem
          ? 'border-slate-200'
          : isApproval
            ? 'border-amber-300'
            : 'border-emerald-400';
        return (
          <div
            key={c.id}
            className={`border-l-4 ${borderColor} pl-3 py-1.5 bg-white rounded-r`}
          >
            <div className="flex items-center justify-between mb-0.5">
              <span
                className={`text-xs ${isSystem ? 'text-slate-400 italic' : 'text-slate-500'}`}
              >
                {kindLabel(c.kind)}
              </span>
              <span className="text-xs text-slate-400">
                {new Date(c.created_at).toLocaleString()}
              </span>
            </div>
            <div className={isSystem ? 'text-slate-600 text-sm italic' : 'text-slate-800'}>
              {isSystem ? <span>{c.body}</span> : <Markdown source={c.body} />}
            </div>
            {c.triggered_continuation && c.triggered_task_id && (
              <div className="mt-1 text-xs text-emerald-700">
                → created follow-up task{' '}
                {projectSlug ? (
                  <Link
                    to={`/p/${projectSlug}/tasks/${c.triggered_task_id}`}
                    className="font-mono underline"
                  >
                    {c.triggered_task_id.slice(0, 8)}
                  </Link>
                ) : (
                  <code className="font-mono">{c.triggered_task_id.slice(0, 8)}</code>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function kindLabel(kind: Comment['kind']): string {
  switch (kind) {
    case 'chat':
      return 'comment';
    case 'approval_request':
      return 'approval requested';
    case 'approval_decision':
      return 'approval decision';
    case 'system_event':
      return 'system';
  }
}
